import Client from 'shopify-buy'
import Cookies from './Cookies.js'
import Signal from './Signal.js'

const productsToItems = products =>
{
    return products.map(product => ({
        id: product.id,
        variants: product.variants.map(variant => ({
            id: variant.id,
            price: Number(variant.price)
        })),
        slug: product.handle,
        title: product.title,
        description: product.description,
        images: product.images.map(image => ({ src: image.src }))
    }))
}

const checkoutToCart = (checkout, items) =>
{
    const list = []

    if (checkout.lineItems) {
        checkout.lineItems.forEach(line =>
        {
            items.forEach(product =>
            {
                product.variants.forEach(variant =>
                {
                    if (variant.id === line.variant.id) {
                        list.push(Object.assign({ quantity: line.quantity }, product))
                    }
                })
            })
        })
    }

    return {
        list,
        paymentDue: checkout.paymentDue,
        totalPrice: checkout.totalPrice,
        totalTax: checkout.totalTax,
        webUrl: checkout.webUrl
    }
}

class Shopify
{
    // Doc https://www.npmjs.com/package/shopify-buy
    constructor()
    {
        if (Shopify._INSTANCE) {
            return Shopify._INSTANCE
        }

        Shopify._INSTANCE = this

        this.isReady = false
        this._isLoading = 0

        this.initListeners()
        this._initShop()
        this._initProducts(() => this._initCheckout())
    }

    get isLoading()
    {
        return this._isLoading > 0
    }

    initListeners()
    {
        this.onItems = new Signal()
        this.onCartUpdated = new Signal()
        this.onError = new Signal()
        this.onLoading = new Signal()
    }

    /* created()
    {
        this.eventHub.$on('shopify:getProduct', this.getProductBySlug)
        this.eventHub.$on('shopify:getItems', this.getItems)
        this.eventHub.$on('shopify:getPolicies', this.getPolicies)
        this.eventHub.$on('shopify:getItemsByCollection', this.getItemsByCollection)
        this.eventHub.$on('shopify:getCategories', this.getCategories)
        this.eventHub.$on('shopify:getCart', this.getCart)
        this.eventHub.$on('shopify:checkout:add', this.addItemsToCart)
        this.eventHub.$on('shopify:checkout:update', this.updateItemsToCart)
    }*/

    // ------------------------
    //      INIT
    // ------------------------

    _changeLoading(add)
    {
        const lastLoad = this._isLoading > 0
        this._isLoading += add

        if (this.isLoading !== (lastLoad > 0)) {
            this.onLoading.dispatch(this.isLoading)
        }
    }

    _initShop(callback = null)
    {
        this._changeLoading(1)

        this._domain = '0do-foods.myshopify.com'
        this._storefrontAccessToken = '9d975da153fff2ae6eb5e8a6177c0cfd'

        this._client = Client.buildClient({
            domain: this._domain,
            storefrontAccessToken: this._storefrontAccessToken
        })

        this._client.shop.fetchInfo()
            .then(infos =>
            {
                this._changeLoading(-1)
                if (callback) {
                    callback(infos)
                }
            })
    }

    _initCheckout(callback = null)
    {
        if (callback) {
            this.onCartUpdated.addOnce(callback)
        }

        this._changeLoading(1)
        if (Cookies.getValue('checkoutId') !== '')
        {
            this._client.checkout
                .fetch(Cookies.getValue('checkoutId'))
                .then(checkout =>
                {
                    this._checkout = checkout

                    this._changeLoading(-1)
                    const cart = checkoutToCart(checkout, productsToItems(this._products))
                    this.onCartUpdated.dispatch(cart)
                })
                .catch(error =>
                {
                    this._changeLoading(-1)
                    console.warn('error when fetching cart', error)
                    this._createNewCheckout()
                    this.onError.dispatch(error)
                })
        }
        else
        {
            this._changeLoading(-1)
            this._createNewCheckout()
        }
    }

    _initProducts(callback)
    {
        this._changeLoading(1)
        this._client.product.fetchAll()
            .then(products =>
            {
                this._products = products

                this._changeLoading(-1)
                const items = productsToItems(products)
                this.onItems.dispatch(items)
                callback(items)
            })
            .catch(error =>
            {
                this._changeLoading(-1)
                console.warn('error when fetching products', error)
                this.onError.dispatch(error)
            })
    }

    // ------------------------
    //      CHECKOUT
    // ------------------------

    _createNewCheckout(callback = null)
    {
        if (callback) {
            this.onCartUpdated.addOnce(callback)
        }

        this._changeLoading(1)
        this._client.checkout.create()
            .then(checkout =>
            {
                this._checkout = checkout
                Cookies.setValue('checkoutId', this._checkout.id, 10)
                // this.onCartUpdated.dispatch(checkout)

                this._changeLoading(-1)
                const cart = checkoutToCart(checkout, productsToItems(this._products))
                this.onCartUpdated.dispatch(cart)
            })
            .catch(error =>
            {
                this._changeLoading(-1)
                console.warn('error when creating new checkout', error)
                this.onError.dispatch(error)
            })
    }

    addItemsToCart(variantId, quantity)
    {
        const items = [{
            variantId,
            quantity
        }]

        this._changeLoading(1)
        this._client.checkout.addLineItems(this._checkout.id, items)
            .then(checkout =>
            {
                this._changeLoading(-1)
                this._checkout = checkout
                // this.addCustomAttributesToCheckout()
                // this.eventHub.$emit('shopify:updateCart', this._checkout, id, quantity)
                // this.onCartUpdated.dispatch(checkout)
                this.getCart(cart => this.onCartUpdated.dispatch(cart))
            })
            .catch(error =>
            {
                this._changeLoading(-1)
                console.warn('error when adding item to cart', error)
                this.onError.dispatch(error)
            })
    }

    updateItemToCart(variantId, quantity)
    {
        const line = this._checkout.lineItems.find(({ variant }) => variant.id === variantId)
        this._changeLoading(1)

        if (line) {
            const data = [
                {
                    id: line.id,
                    quantity
                }
            ]
            this._client.checkout.updateLineItems(this._checkout.id, data)
                .then(checkout =>
                {
                    this._changeLoading(-1)
                    this._checkout = checkout
                    this.getCart(cart => this.onCartUpdated.dispatch(cart))
                })
                .catch(error =>
                {
                    this._changeLoading(-1)
                    console.warn('error when update item from cart', error)
                    this.onError.dispatch(error)
                })
        } else {
            this._changeLoading(-1)
        }
    }

    removeItemToCart(variantId)
    {
        const line = this._checkout.lineItems.find(({ variant }) => variant.id === variantId)
        this._changeLoading(1)

        if (line) {
            this._client.checkout.removeLineItems(this._checkout.id, line.id)
                .then(checkout =>
                {
                    this._changeLoading(-1)
                    this._checkout = checkout
                    this.getCart(cart => this.onCartUpdated.dispatch(cart))
                })
                .catch(error =>
                {
                    this._changeLoading(-1)
                    console.warn('error when remove item from cart', error)
                    this.onError.dispatch(error)
                })
        } else {
            this._changeLoading(-1)
        }
    }


    // ------------------------
    //      GETTERS
    // ------------------------

    getItems(callback)
    {
        this._changeLoading(1)
        const dispatch = items =>
        {
            this._changeLoading(-1)
            callback(items)
        }

        if (this._products) {
            dispatch(productsToItems(this._products))
        } else {
            this.onItems.addOnce(dispatch)
        }
    }

    getCart(callback)
    {
        this._changeLoading(1)
        const dispatch = cart =>
        {
            this._changeLoading(-1)
            callback(cart)
        }

        if (this._checkout) {
            this.getItems(items =>
            {
                dispatch(checkoutToCart(this._checkout, items))
            })
        } else {
            this.onCartUpdated.addOnce(dispatch)
        }
    }

    getPolicies(callback)
    {
        this._changeLoading(1)
        this._client.shop.fetchPolicies()
            .then(policies =>
            {
                this._changeLoading(-1)
                callback(policies)
            })
            .catch(error =>
            {
                this._changeLoading(-1)
                console.warn('error when fetching policies', error)
                this.onError.dispatch(error)
            })
    }

    getProductBySlug(slug, callback)
    {
        this._changeLoading(1)
        this._client.product.fetchByHandle(slug)
            .then(product =>
            {
                this._changeLoading(-1)
                callback(productsToItems([product])[0])
            })
            .catch(error =>
            {
                this._changeLoading(-1)
                console.warn('error when fetching product', error)
                this.onError.dispatch(error)
                // router.push('/404')
            })
    }

    /*getCategories(callback)
    {
        this._client.collection.fetchAll()
            .then(collections =>
            {
                this.categories = collections
                callback(callback)
            })
            .catch(error =>
            {
                console.warn('error when fetching collections', error)
            })
    }*/

    /*getItemsByCollection(collection)
    {
        let isError = true

        this.categories.forEach(category =>
        {
            if (collection === category.handle)
            {
                isError = false
                this._client.collection.fetchWithProducts(category.id).then(collection =>
                {
                    this.eventHub.$emit('shopify:setProducts', collection.products)
                })
            }
        })

        if (isError)
        {
            router.push('/404')
        }
    }*/
}

export default Shopify
